import { isEqual } from "lodash";
import { ConditionKey, FlowNode, Integration, IntegrationType } from "@/api/types";
import {
  AutomationActionType,
  AutomationTrigger,
  AutomationTriggerType,
} from "@/api/types/automation";

export type TriggerOption = {
  title: string;
  description: string;
  id: string;
  source: IntegrationType;
  trigger: AutomationTrigger;
};

export const TRIGGER_OPTIONS: TriggerOption[] = [
  {
    title: "New incoming text message",
    description: "A contact sends a text message.",
    id: AutomationTriggerType.incomingMessage,
    source: IntegrationType.helios,
    trigger: {
      type: AutomationTriggerType.incomingMessage,
      conditions: [
        {
          key: "message.body",
          operators: [{ op: "$regex", value: "hello|world" }],
        },
      ],
    },
  },
  {
    title: "Form submission",
    description: "A contact submits a form.",
    id: AutomationTriggerType.formSubmission,
    source: IntegrationType.helios,
    trigger: {
      type: AutomationTriggerType.formSubmission,
      conditions: [
        {
          key: "submission.id",
          operators: [{ op: "$exists", value: true }],
        },
      ],
    },
  },
  {
    title: "Added to list",
    description: "A contact is added to a list.",
    id: AutomationTriggerType.addedToList,
    source: IntegrationType.helios,
    trigger: {
      type: AutomationTriggerType.addedToList,
      conditions: [
        {
          key: "list",
          operators: [{ op: "$eq", value: "" }],
        },
      ],
    },
  },
  {
    title: "Removed from list",
    description: "A contact is removed from a list.",
    id: AutomationTriggerType.removedFromList,
    source: IntegrationType.helios,
    trigger: {
      type: AutomationTriggerType.removedFromList,
      conditions: [
        {
          key: "list",
          operators: [{ op: "$eq", value: "" }],
        },
      ],
    },
  },
  {
    title: "Customer event",
    description: "A contact performs a custom event.",
    id: AutomationTriggerType.customerEvent,
    source: IntegrationType.helios,
    trigger: {
      type: AutomationTriggerType.customerEvent,
      conditions: [
        {
          key: "event.type",
          operators: [{ op: "$eq", value: "" }],
        },
        {
          key: "event.event",
          operators: [{ op: "$eq", value: "" }],
        },
      ],
    },
  },
  {
    title: "Reservation assigned from waitlist",
    description: "A reservation is assigned from the waitlist.",
    id: "mariana-tek-reservation_assignedtospot",
    source: IntegrationType.marianaTek,
    trigger: {
      type: AutomationTriggerType.customerEvent,
      conditions: [
        {
          key: "event.type",
          operators: [{ op: "$eq", value: "mariana-tek" }],
        },
        {
          key: "event.event",
          operators: [{ op: "$eq", value: "reservation_assignedtospot" }],
        },
        {
          key: "event.properties.assigned_from_waitlist",
          operators: [{ op: "$eq", value: true }],
        },
      ],
    },
  },
  {
    title: "Checkout on a Helios page",
    description: "A contact completes checkout on a Helios booking page.",
    id: "helios-pixel-checkout_completed",
    source: IntegrationType.helios,
    trigger: {
      type: AutomationTriggerType.customerEvent,
      conditions: [
        {
          key: "event.type",
          operators: [{ op: "$eq", value: "helios-pixel" }],
        },
        {
          key: "event.event",
          operators: [{ op: "$eq", value: "checkout_completed" }],
        },
      ],
    },
  },
  {
    title: "Account created on a Helios page",
    description: "A contact creates an account on a Helios booking page.",
    id: "helios-pixel-account_created",
    source: IntegrationType.helios,
    trigger: {
      type: AutomationTriggerType.customerEvent,
      conditions: [
        {
          key: "event.type",
          operators: [{ op: "$eq", value: "helios-pixel" }],
        },
        {
          key: "event.event",
          operators: [{ op: "$eq", value: "account_created" }],
        },
      ],
    },
  },
];

export const CONDITION_KEYS: { [key: string]: ConditionKey[] } = {
  incoming_message: [
    {
      label: "Message Body",
      description: "The body of the message",
      value: "message.body",
      source: "helios",
      type: "string",
    },
  ],
  added_to_list: [
    {
      label: "List",
      description: "List that contact is added to",
      value: "list",
      source: "helios",
      type: "string",
    },
  ],
  removed_from_list: [
    {
      label: "List",
      description: "List that contact is removed from",
      value: "list",
      source: "helios",
      type: "string",
    },
  ],
  form_submission: [
    {
      label: "Submission",
      description: "Any form submission event",
      value: "submission.id",
      source: "helios",
      type: "string",
    },
  ],
  customer_event: [
    {
      label: "Event Type",
      description: "Integration that produced the customer event",
      value: "event.type",
      source: "helios",
      type: "string",
    },
    {
      label: "Event",
      description: "Name of customer event",
      value: "event.event",
      source: "helios",
      type: "string",
    },
  ],
};

export function findTriggerOptionByTrigger(
  trigger: AutomationTrigger,
  options: TriggerOption[] = TRIGGER_OPTIONS,
) {
  return (
    options.find(
      (option) =>
        option.trigger.type === trigger.type &&
        isEqual(option.trigger.conditions, trigger.conditions),
    ) ?? options.find((option) => option.trigger.type === trigger.type)
  );
}

export type ActionOption = {
  title: string;
  description: string;
  id: string;
  /**
   * Distinct key for the insert picker when multiple options map to the same
   * action `id` (e.g. SMS vs email both create `outgoing_message`). Falls back
   * to `id` when omitted.
   */
  pickerId?: string;
  source: IntegrationType | "time" | "logic";
  node: Omit<FlowNode, "id" | "children">;
};

export const SET_PROPERTY_KEY_PLACEHOLDER = "__set_property_key__";

export const SEND_EMAIL_PICKER_ID = "send_email";

/**
 * Whether a palette option can only be offered to gyms with Helios email
 * enabled: the email step itself, and the text-or-email step whose email half
 * depends on it.
 */
export function actionOptionRequiresEmail(option: ActionOption): boolean {
  return (
    option.pickerId === SEND_EMAIL_PICKER_ID ||
    option.id === AutomationActionType.sendMessageMultichannel
  );
}

export const ACTION_OPTIONS: ActionOption[] = [
  {
    title: "Send text message",
    description: "Send an SMS to the contact.",
    id: AutomationActionType.sendMessage,
    source: IntegrationType.helios,
    node: {
      type: "action",
      options: {
        actions: [
          {
            type: AutomationActionType.sendMessage,
            options: {
              body: "",
            },
          },
        ],
      },
    },
  },
  {
    title: "Send email",
    description: "Rich HTML or plain-text email to the contact.",
    id: AutomationActionType.sendMessage,
    pickerId: SEND_EMAIL_PICKER_ID,
    source: IntegrationType.helios,
    node: {
      type: "action",
      options: {
        actions: [
          {
            type: AutomationActionType.sendMessage,
            options: {
              protocol: "email",
              subject: "",
              body: "",
            },
          },
        ],
      },
    },
  },
  {
    title: "Send text or email",
    description: "Texts the contact when possible, otherwise emails them.",
    id: AutomationActionType.sendMessageMultichannel,
    source: IntegrationType.helios,
    node: {
      type: "action",
      options: {
        actions: [
          {
            type: AutomationActionType.sendMessageMultichannel,
            options: {
              sms: { body: "" },
              email: { subject: "", body: "" },
            },
          },
        ],
      },
    },
  },
  {
    title: "Add to list",
    description: "Add the contact to a list.",
    id: AutomationActionType.addToList,
    source: IntegrationType.helios,
    node: {
      type: "action",
      options: {
        actions: [
          {
            type: AutomationActionType.addToList,
            options: { list: "" },
          },
        ],
      },
    },
  },
  {
    title: "Remove from list",
    description: "Remove the contact from a list.",
    id: AutomationActionType.removeFromList,
    source: IntegrationType.helios,
    node: {
      type: "action",
      options: {
        actions: [
          {
            type: AutomationActionType.removeFromList,
            options: { list: "" },
          },
        ],
      },
    },
  },
  {
    title: "Add tags",
    description: "Add tags to the contact.",
    id: AutomationActionType.addTags,
    source: IntegrationType.helios,
    node: {
      type: "action",
      options: {
        actions: [
          {
            type: AutomationActionType.addTags,
            options: { tags: [] },
          },
        ],
      },
    },
  },
  {
    title: "Remove tags",
    description: "Remove tags from the contact.",
    id: AutomationActionType.removeTags,
    source: IntegrationType.helios,
    node: {
      type: "action",
      options: {
        actions: [
          {
            type: AutomationActionType.removeTags,
            options: { tags: [] },
          },
        ],
      },
    },
  },
  {
    title: "Create task",
    description: "Create a follow-up task for your team.",
    id: AutomationActionType.createTask,
    source: IntegrationType.helios,
    node: {
      type: "action",
      options: {
        actions: [
          {
            type: AutomationActionType.createTask,
            options: { title: "" },
          },
        ],
      },
    },
  },
  {
    title: "Webhook",
    description: "Send an HTTP request to an external URL.",
    id: AutomationActionType.webhook,
    source: IntegrationType.helios,
    node: {
      type: "action",
      options: {
        actions: [
          {
            type: AutomationActionType.webhook,
            options: {
              url: "https://example.com/webhook",
              payload: {},
            },
          },
        ],
      },
    },
  },
  {
    title: "Wait",
    description: "Pause the flow before the next step.",
    id: "timer",
    source: "time",
    node: {
      type: "timer",
      options: {
        interval: { days: 1 },
      },
    },
  },
];

/**
 * "Actions" palette/menu section: every action step except the time-based
 * ones (Wait), which render under their own "Time" section.
 */
export const ACTION_SECTION_OPTIONS: ActionOption[] = ACTION_OPTIONS.filter(
  (option) => option.source !== "time",
);

/** "Time" palette/menu section: time-based steps (e.g. Wait). */
export const TIME_SECTION_OPTIONS: ActionOption[] = ACTION_OPTIONS.filter(
  (option) => option.source === "time",
);

export type NodeTypeOption = {
  title: string;
  description: string;
  id: string;
  node: Omit<FlowNode, "id" | "children">;
};

export const NODE_TYPE_OPTIONS: NodeTypeOption[] = [
  {
    title: "Filter",
    description: "Filter out contacts.",
    id: "filter",
    node: { type: "filter", options: { rules: [] } },
  },
  {
    title: "Conditional",
    description: "If / else branch.",
    id: "conditional",
    node: { type: "conditional", options: { rules: [] } },
  },
  // NOTE: The "branch" (multi-path split) node is intentionally omitted from
  // the add-step palette and insert menus — it's too complex for our users.
  // The node type, renderer, editor, and validation remain in place so any
  // flows that already contain branch nodes keep rendering and editing
  // correctly; we only prevent authoring new ones.
];

export type GroupedOption<T> = {
  label: string;
  options: T[];
};

export function triggerOptions(props: { installedIntegrations?: Integration[] }): {
  options: TriggerOption[];
  groupedOptions: GroupedOption<TriggerOption>[];
} {
  function groupBySource(triggerOptions: TriggerOption[]): GroupedOption<TriggerOption>[] {
    return Object.values(
      triggerOptions.reduce(
        (grouped, option) => {
          (grouped[option.source] = grouped[option.source] || {
            label: option.source,
            options: [],
          }).options.push(option);
          return grouped;
        },
        {} as Record<string, GroupedOption<TriggerOption>>,
      ),
    );
  }

  const integrationTypes = props.installedIntegrations?.map((integration) => integration.type);
  const options: TriggerOption[] = TRIGGER_OPTIONS.filter(
    (option) =>
      option.source === "helios" ||
      integrationTypes?.includes(option.source as Integration["type"]),
  );
  const groupedOptions = groupBySource(options);

  return {
    options,
    groupedOptions,
  };
}

export function actionOptions(props: { installedIntegrations?: Integration[] }): {
  options: ActionOption[];
  groupedOptions: GroupedOption<ActionOption>[];
} {
  function groupBySource(actionOptions: ActionOption[]): GroupedOption<ActionOption>[] {
    return Object.values(
      actionOptions.reduce(
        (grouped, option) => {
          (grouped[option.source] = grouped[option.source] || {
            label: option.source,
            options: [],
          }).options.push(option);
          return grouped;
        },
        {} as Record<string, GroupedOption<ActionOption>>,
      ),
    );
  }

  const integrationTypes = props.installedIntegrations?.map((integration) => integration.type);
  const options: ActionOption[] = ACTION_OPTIONS.filter(
    (option) =>
      option.source === "helios" ||
      option.source === "time" ||
      option.source === "logic" ||
      integrationTypes?.includes(option.source as Integration["type"]),
  );
  const groupedOptions = groupBySource(options);

  return {
    options,
    groupedOptions,
  };
}
